import type { CSSProperties } from 'react'
import { MobileTabBar, mobileTabBarIcons } from '@/widgets/mobile-tab-bar'
import { Button, ContentGroup, InputField } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import { routes } from '@/shared/config'

type DashboardProfileMobileProps = {
  className?: string
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.onboarding.titleMobile),
  textAlign: 'center' as const,
}

const sectionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.account.referralFaqTitleMobile),
}

const labelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.account.profileLabel),
}

const inputTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.black,
  ...typographyStyle(designTokens.typography.body.smTight),
}

type InputTokenStyle = CSSProperties & { ['--input-placeholder']?: string }

const inputBaseStyle: CSSProperties = {
  height: designTokens.components.account.userMenu.inputHeight,
  borderRadius: designTokens.borders.radius.sm,
  borderWidth: designTokens.borders.widths.thin,
  borderStyle: designTokens.borders.styles.solid,
  backgroundColor: designTokens.colors.background.surface,
  padding: `${designTokens.spacing.sm} ${designTokens.spacing.xs}`,
  ...inputTextStyle,
}

const inputPlaceholderStyle: InputTokenStyle = {
  '--input-placeholder': designTokens.colors.text.inputPlaceholder,
}

const inputClassName =
  'placeholder:text-[var(--input-placeholder)] focus-visible:ring-0 focus-visible:ring-offset-0'

const buildInputStyle = (borderColor: string): InputTokenStyle => ({
  ...inputBaseStyle,
  ...inputPlaceholderStyle,
  borderColor,
})

const calloutCardStyle: CSSProperties = {
  backgroundColor: designTokens.colors.background.profileCard,
  borderRadius: designTokens.borders.radius.lg,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  boxShadow: designTokens.shadows.adminCard,
  padding: `${designTokens.spacing.xxl} ${designTokens.spacing.md}`,
  gap: designTokens.spacing.lg,
}

const calloutTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.account.cardTitle),
}

const calloutBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.base),
  whiteSpace: 'pre-wrap' as const,
}

const calloutButtonStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  minHeight: designTokens.components.account.referral.heroButtonHeight,
}

export function DashboardProfileMobile({ className }: DashboardProfileMobileProps) {
  return (
    <div className={cn('flex w-full flex-col lg:hidden', className)}>
      <div
        className="flex w-full flex-col"
        style={{ paddingBottom: designTokens.spacing.popupModalMobileBottomOffset }}
      >
        <ContentGroup variant="planSelectorMobile" className="w-full">
          <div className="flex w-full items-center justify-center">
            <p style={titleStyle}>Мой профиль</p>
          </div>
          <ContentGroup variant="transparentPanelMobile" className="w-full">
            <p style={sectionTitleStyle}>Личная информация</p>
            <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxl }}>
              <InputField
                label="Имя"
                defaultValue="Екатерина"
                containerClassName="w-full"
                labelStyle={labelStyle}
                containerStyle={{ gap: designTokens.spacing.xs }}
                inputClassName={inputClassName}
                style={buildInputStyle(designTokens.colors.border.inputFocus)}
              />
              <InputField
                label="Фамилия"
                placeholder="Иванова"
                containerClassName="w-full"
                labelStyle={labelStyle}
                containerStyle={{ gap: designTokens.spacing.xs }}
                inputClassName={inputClassName}
                style={buildInputStyle(designTokens.colors.border.inputDefault)}
              />
            </div>
          </ContentGroup>
          <ContentGroup variant="transparentPanelMobile" className="w-full">
            <p style={sectionTitleStyle}>Адрес почты для входа</p>
            <InputField
              label="Email"
              defaultValue="email@domain.com"
              containerClassName="w-full"
              labelStyle={labelStyle}
              containerStyle={{ gap: designTokens.spacing.xs }}
              inputClassName={inputClassName}
              style={buildInputStyle(designTokens.colors.border.inputFocus)}
            />
            <Button
              type="button"
              variant="secondarySubmitMobile"
              className="w-full"
              style={calloutButtonStyle}
            >
              Изменить адрес почты
            </Button>
          </ContentGroup>
          <div className="flex w-full flex-col" style={calloutCardStyle}>
            <p style={calloutTitleStyle}>Хотите делиться опытом в подписке?</p>
            <p style={calloutBodyStyle}>
              Пожалуйста, заполните форму на сайте.
              {'\n'}
              Мы тщательно отбираем экспертов и спикеров, и обязательно свяжемся с вами, если у нас
              случится «мэтч».
            </p>
            <Button
              type="button"
              variant="secondarySubmitMobile"
              className="w-full"
              style={calloutButtonStyle}
            >
              Поработать с Epic Growth
            </Button>
          </div>
        </ContentGroup>
      </div>
      <MobileTabBar
        className="lg:hidden"
        activeKey="profile"
        items={[
          { key: 'home', label: 'Главная', icon: mobileTabBarIcons.home, href: routes.dashboardLk },
          {
            key: 'subscription',
            label: 'Подписка',
            icon: mobileTabBarIcons.subscription,
            href: routes.dashboardSubscription,
          },
          {
            key: 'profile',
            label: 'Профиль',
            icon: mobileTabBarIcons.profile,
            href: routes.dashboardProfile,
          },
        ]}
      />
    </div>
  )
}

DashboardProfileMobile.displayName = 'DashboardProfileMobile'
